/* whero-bundle-builder.js — picker behavior for sections/whero-bundle-builder.liquid.
 *
 * Reads its config from the JSON island the section emits (settings,
 * labels, and flavors). Server-rendered card markup is the source of
 * truth for first paint; this script:
 *   - tracks picks ({ variant_id: qty })
 *   - swaps the card action area HTML on add/remove using labels from
 *     the JSON island (no hardcoded English customer-facing copy)
 *   - re-renders the tray (count, progress bar, slots, CTA copy/state)
 *   - fires /cart/add.js on submit with _bundle_components + Pint N
 *     line-item properties per docs/bundle-builder-plan.md §4
 *   - filters cards by category (reads __cat-- modifier class)
 *   - "Surprise me" picks N random in-stock flavors
 *   - mobile drawer expand/collapse
 *
 * Multiple bundle sections on one page are supported — each instance
 * scopes selectors via [data-bundle-builder-section="<id>"].
 */

(function () {
  'use strict';

  document.querySelectorAll('[data-bundle-builder-section]').forEach(initSection);

  function initSection(section) {
    var dataEl = section.querySelector('script[data-bundle-data]');
    if (!dataEl) return;

    var DATA;
    try {
      DATA = JSON.parse(dataEl.textContent);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[bundle-builder] failed to parse JSON island', err);
      return;
    }

    var SIZE = DATA.bundle_size;
    var PRICE = DATA.bundle_price;
    var PARENT_ID = DATA.parent_variant_id;
    // Shared "Custom Pint" placeholder variant. Rotation flavors with no
    // backing Shopify product are routed to this variant so they still
    // become real, itemized child lines (carrying the flavor name). Null
    // when unset → those picks fall back to manifest-only preservation.
    var PLACEHOLDER = (typeof DATA.rotation_placeholder_id === 'number' && DATA.rotation_placeholder_id > 0)
      ? DATA.rotation_placeholder_id
      : null;
    var LABELS = DATA.labels || {};
    var FLAVORS = Array.isArray(DATA.flavors) ? DATA.flavors : [];

    var FLAVORS_BY_VARIANT = {};
    FLAVORS.forEach(function (f) { FLAVORS_BY_VARIANT[String(f.variant_id)] = f; });

    var picks = {};        // { variantId: qty }
    var activeFilter = 'all';
    var submitting = false;

    /* ---------- helpers --------------------------------------------- */

    function totalPicked() {
      var total = 0;
      Object.keys(picks).forEach(function (k) { total += picks[k]; });
      return total;
    }

    function isFull() { return totalPicked() >= SIZE; }

    /* Per-flavor pick cap from the JSON island (max_qty). Numeric only
     * for tracked, deny-policy products — checkout can't stop expanded
     * bundle components from overselling, so the picker must. Toast-
     * rotation flavors and untracked products carry null = uncapped. */
    function maxFor(id) {
      var f = FLAVORS_BY_VARIANT[String(id)];
      return (f && typeof f.max_qty === 'number') ? f.max_qty : Infinity;
    }

    function escapeHtml(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    /* fillTemplate — see docs/bundle-builder-plan.md §3.5.
     * Replaces {size}, {remaining}, {count}, {price}, {s}.
     * vars.pluralBase drives {s}: empty when ===1, else 's'.
     */
    function fillTemplate(t, vars) {
      vars = vars || {};
      return String(t || '')
        .replace(/\{size\}/g, vars.size != null ? vars.size : SIZE)
        .replace(/\{remaining\}/g, vars.remaining != null ? vars.remaining : '')
        .replace(/\{count\}/g, vars.count != null ? vars.count : '')
        .replace(/\{price\}/g, vars.price != null ? vars.price : '')
        .replace(/\{s\}/g, vars.pluralBase === 1 ? '' : 's');
    }

    function getCardCategory(card) {
      var eyebrow = card.querySelector('.whero-flavor-card__cat');
      if (!eyebrow) return null;
      var match = eyebrow.className.match(/whero-flavor-card__cat--([\w-]+)/);
      return match ? match[1] : null;
    }

    function isCardSoldOut(card) { return card.classList.contains('is-soldout'); }

    /* ---------- card action area rendering -------------------------- */

    function renderActionArea(variantId, soldOut, qty, full) {
      var lc = LABELS.card || {};
      if (soldOut) {
        return '<button class="whero-flavor-card__add-btn" disabled>'
          + escapeHtml(lc.sold_out)
          + '</button>';
      }
      if (qty > 0) {
        var atCap = qty >= maxFor(variantId);
        return '<div class="whero-flavor-card__stepper" role="group" aria-label="Quantity">'
          +   '<button data-act="dec" data-id="' + escapeHtml(variantId) + '" aria-label="Remove one">−</button>'
          +   '<div class="qty">' + qty + '<small>' + escapeHtml(lc.in_box) + '</small></div>'
          +   '<button data-act="inc" data-id="' + escapeHtml(variantId) + '" aria-label="Add one"' + ((full || atCap) ? ' disabled' : '') + '>+</button>'
          + '</div>';
      }
      if (full) {
        return '<button class="whero-flavor-card__add-btn" disabled>'
          + escapeHtml(lc.full)
          + '</button>';
      }
      return '<button class="whero-flavor-card__add-btn" data-act="add" data-id="' + escapeHtml(variantId) + '">'
        + escapeHtml(lc.add)
        + '</button>';
    }

    function rerenderCard(card) {
      var variantId = card.getAttribute('data-id');
      if (!variantId) return;
      var qty = picks[variantId] || 0;
      var soldOut = isCardSoldOut(card);
      var full = isFull();

      card.classList.toggle('is-selected', qty > 0);
      card.classList.toggle('is-disabled', !soldOut && full && qty === 0);

      var pip = card.querySelector('.whero-flavor-card__check-pip');
      if (pip) pip.textContent = qty;

      var action = card.querySelector('.whero-flavor-card__action');
      if (action) action.innerHTML = renderActionArea(variantId, soldOut, qty, full);
    }

    function rerenderAllCards() {
      section.querySelectorAll('.whero-flavor-card[data-id]').forEach(rerenderCard);
    }

    /* ---------- tray rendering -------------------------------------- */

    function rerenderTray() {
      var lt = LABELS.tray || {};
      var total = totalPicked();
      var remaining = SIZE - total;

      var countEl = section.querySelector('[data-tray-count]');
      if (countEl) countEl.textContent = total + ' / ' + SIZE;

      var bar = section.querySelector('[data-tray-progress]');
      if (bar) bar.style.width = (total / SIZE * 100) + '%';

      // ordered slot list (flatten picks by qty so e.g. {a: 2} renders a, a)
      var orderedSlots = [];
      Object.keys(picks).forEach(function (vid) {
        var f = FLAVORS_BY_VARIANT[vid];
        var qty = picks[vid];
        for (var i = 0; i < qty; i++) orderedSlots.push(f);
      });

      var slotList = section.querySelector('[data-tray-slots]');
      if (slotList) {
        var html = '';
        for (var i = 0; i < SIZE; i++) {
          var f = orderedSlots[i];
          var filled = !!f;
          html += '<li class="whero-bundle-builder__slot' + (filled ? ' is-filled' : '') + '">'
            +   '<span class="whero-bundle-builder__slot-num">' + (i + 1) + '</span>'
            +   '<span class="whero-bundle-builder__slot-name">'
            +     escapeHtml(filled ? f.name : (lt.slot_empty || ''))
            +   '</span>'
            +   '<button type="button" class="whero-bundle-builder__slot-remove" '
            +     'data-act="dec" data-id="' + (filled ? escapeHtml(f.variant_id) : '') + '" '
            +     'aria-label="Remove pint">×</button>'
            + '</li>';
        }
        slotList.innerHTML = html;
      }

      var cta = section.querySelector('[data-tray-cta]');
      if (cta) {
        if (total === 0) {
          cta.textContent = fillTemplate(lt.cta_empty, { size: SIZE });
          cta.disabled = true;
        } else if (total < SIZE) {
          cta.textContent = fillTemplate(lt.cta_partial, {
            remaining: remaining,
            pluralBase: remaining
          });
          cta.disabled = true;
        } else {
          cta.textContent = fillTemplate(lt.cta_full, {
            size: SIZE,
            price: PRICE != null ? Number(PRICE).toFixed(2) : ''
          });
          cta.disabled = submitting;
        }
      }
    }

    /* ---------- pick state mutators --------------------------------- */

    function add(id) {
      if (totalPicked() >= SIZE) return;
      var cap = maxFor(id);
      if ((picks[id] || 0) >= cap) {
        var msg = (LABELS.tray && LABELS.tray.only_left) || '';
        if (msg) showToast(fillTemplate(msg, { count: cap, pluralBase: cap }));
        return;
      }
      picks[id] = (picks[id] || 0) + 1;
      rerenderAllCards();
      rerenderTray();
    }

    function remove(id) {
      if (!picks[id]) return;
      picks[id] -= 1;
      if (picks[id] <= 0) delete picks[id];
      rerenderAllCards();
      rerenderTray();
    }

    /* ---------- toast ------------------------------------------------ */

    var toastTimer = null;
    function showToast(msg) {
      var toast = section.querySelector('[data-bundle-toast]');
      var toastMsg = section.querySelector('[data-bundle-toast-msg]');
      if (!toast || !toastMsg) return;
      toastMsg.textContent = msg;
      toast.classList.add('is-showing');
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(function () {
        toast.classList.remove('is-showing');
      }, 2200);
    }

    /* ---------- event wiring ---------------------------------------- */

    // Card + tray button delegation (add / inc / dec)
    section.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-act]');
      if (!btn) return;
      var act = btn.getAttribute('data-act');
      var id = btn.getAttribute('data-id');
      if (!id) return;
      if (act === 'add' || act === 'inc') add(id);
      else if (act === 'dec') remove(id);
    });

    // Filter chips
    section.querySelectorAll('.whero-bundle-builder__chip[data-filter]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        section.querySelectorAll('.whero-bundle-builder__chip').forEach(function (c) {
          c.classList.remove('is-active');
          c.setAttribute('aria-selected', 'false');
        });
        chip.classList.add('is-active');
        chip.setAttribute('aria-selected', 'true');
        activeFilter = chip.getAttribute('data-filter');
        applyFilter();
      });
    });

    function applyFilter() {
      section.querySelectorAll('.whero-flavor-grid__item').forEach(function (li) {
        var card = li.querySelector('.whero-flavor-card');
        if (!card) return;
        var cat = getCardCategory(card);
        var visible = (activeFilter === 'all') || (cat === activeFilter);
        li.style.display = visible ? '' : 'none';
      });
    }

    // Surprise Me
    var surpriseBtn = section.querySelector('[data-surprise]');
    if (surpriseBtn) {
      surpriseBtn.addEventListener('click', function () {
        var pool = FLAVORS.filter(function (f) { return f.is_available_today; });
        if (pool.length === 0) return;

        Object.keys(picks).forEach(function (k) { delete picks[k]; });
        for (var i = 0; i < SIZE; i++) {
          // Respect per-flavor stock caps (max_qty) while filling.
          var candidates = pool.filter(function (f) {
            var key = String(f.variant_id);
            return (picks[key] || 0) < maxFor(key);
          });
          if (candidates.length === 0) break;
          var pick = candidates[Math.floor(Math.random() * candidates.length)];
          var key = String(pick.variant_id);
          picks[key] = (picks[key] || 0) + 1;
        }
        rerenderAllCards();
        rerenderTray();

        var msg = (LABELS.tray && LABELS.tray.surprise_msg) || '';
        if (msg) showToast(msg);
      });
    }

    // Gift message — live char count
    var giftInput = section.querySelector('[data-gift-message]');
    var giftCount = section.querySelector('[data-gift-message-count]');
    if (giftInput && giftCount) {
      giftInput.addEventListener('input', function () {
        giftCount.textContent = giftInput.value.length;
      });
    }

    // Submit → /cart/add.js
    var ctaBtn = section.querySelector('[data-tray-cta]');
    if (ctaBtn) {
      ctaBtn.addEventListener('click', function () {
        if (totalPicked() < SIZE || submitting) return;

        if (!PARENT_ID) {
          showToast('Bundle parent variant id missing — check product setup');
          return;
        }

        // Build _bundle_components and Pint N props in slot order.
        //
        // Every pick becomes a component the cart-transform expands into a
        // real child line. Picks backed by a Shopify product carry that
        // product's numeric variant_id. Rotation picks (whero_flavor
        // metaobjects with no backing product) have a string handle and no
        // variant of their own, so they're routed to the shared "Custom
        // Pint" placeholder variant (PLACEHOLDER), flagged `custom: true`
        // with their real flavor name — the function expands those into
        // placeholder lines that surface the flavor as a `Flavor` line
        // property + decrement nothing (placeholder inventory untracked).
        // If PLACEHOLDER isn't configured, rotation picks are skipped by
        // the function and survive only via Pint N + _bundle_manifest.
        var components = [];
        var pintProps = {};
        var pintIndex = 1;
        var perVariantOrdered = [];
        Object.keys(picks).forEach(function (id) {
          var qty = picks[id];
          var f = FLAVORS_BY_VARIANT[id];
          if (!f) return;
          if (typeof f.variant_id === 'number') {
            components.push({ variant_id: f.variant_id, qty: qty, name: f.name });
          } else if (PLACEHOLDER) {
            components.push({ variant_id: PLACEHOLDER, qty: qty, name: f.name, custom: true });
          }
          for (var i = 0; i < qty; i++) perVariantOrdered.push(f);
        });
        perVariantOrdered.forEach(function (f) {
          if (!f) return;
          pintProps['Pint ' + pintIndex] = f.name;
          pintIndex += 1;
        });

        // Full-box manifest of EVERY pick, collapsed to "Name ×N" in
        // first-appearance order. The cart-transform re-attaches this to
        // a child line as a visible property so the complete 6-pack
        // survives expansion. Without it, rotation-flavor picks (string
        // handle, no numeric variant, so absent from _bundle_components)
        // are dropped along with the parent line and vanish from the
        // order — which is how 6-pick boxes shipped as 4 and 5.
        // See apps/wynstons-bundles/.../cart_transform_run.js.
        var manifestOrder = [];
        var manifestQty = {};
        perVariantOrdered.forEach(function (f) {
          if (!f) return;
          if (manifestQty[f.name] == null) {
            manifestQty[f.name] = 0;
            manifestOrder.push(f.name);
          }
          manifestQty[f.name] += 1;
        });
        var manifestStr = manifestOrder.map(function (n) {
          return manifestQty[n] > 1 ? (n + ' ×' + manifestQty[n]) : n;
        }).join(', ');

        var properties = Object.assign(
          { '_bundle_components': JSON.stringify(components) },
          pintProps
        );
        if (manifestStr) properties['_bundle_manifest'] = manifestStr;

        // Gift message — optional line-item property. Trim, drop if empty.
        // Property name has no leading underscore so it surfaces on the
        // customer's order confirmation + packing slip, not just admin.
        var giftEl = section.querySelector('[data-gift-message]');
        if (giftEl) {
          var giftMsg = (giftEl.value || '').trim();
          if (giftMsg) {
            properties['Gift message'] = giftMsg.slice(0, 200);
          }
        }

        submitting = true;
        ctaBtn.disabled = true;

        var cartPayload = {
          items: [{
            id: PARENT_ID,
            quantity: 1,
            properties: properties
          }]
        };
        // eslint-disable-next-line no-console
        console.log('[bundle-builder] /cart/add.js payload', cartPayload);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(cartPayload)
        })
          .then(function (r) {
            return r.text().then(function (body) {
              // eslint-disable-next-line no-console
              console.log('[bundle-builder] /cart/add.js response', r.status, body);
              if (!r.ok) {
                var msg = 'cart add failed (' + r.status + ')';
                try {
                  var parsed = JSON.parse(body);
                  if (parsed && parsed.description) msg += ': ' + parsed.description;
                  else if (parsed && parsed.message) msg += ': ' + parsed.message;
                } catch (e) { /* non-JSON body, ignore */ }
                throw new Error(msg);
              }
              return body ? JSON.parse(body) : null;
            });
          })
          .then(function () {
            window.location.href = '/cart';
          })
          .catch(function (err) {
            // eslint-disable-next-line no-console
            console.error('[bundle-builder] cart add failed', err);
            showToast(err && err.message ? err.message : 'Cart add failed');
            submitting = false;
            ctaBtn.disabled = false;
          });
      });
    }

    /* ---------- mobile drawer --------------------------------------- */

    var tray = section.querySelector('[data-bundle-tray]');
    var trayToggle = section.querySelector('[data-tray-toggle]');
    var userExpanded = false;

    function isMobile() {
      return window.matchMedia('(max-width: 960px)').matches;
    }

    function setToggleLabel() {
      if (!trayToggle || !tray) return;
      var collapsed = tray.classList.contains('is-collapsed');
      var lt = LABELS.tray || {};
      trayToggle.textContent = collapsed ? (lt.expand || '') : (lt.collapse || '');
      trayToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    if (trayToggle && tray) {
      trayToggle.addEventListener('click', function () {
        tray.classList.toggle('is-collapsed');
        userExpanded = !tray.classList.contains('is-collapsed');
        setToggleLabel();
      });
    }

    function applyMobileTrayState() {
      if (!tray) return;
      if (isMobile()) {
        if (totalPicked() === 0 && !userExpanded) {
          tray.classList.add('is-collapsed');
        }
      } else {
        tray.classList.remove('is-collapsed');
      }
      setToggleLabel();
    }

    window.addEventListener('resize', applyMobileTrayState);

    /* ---------- initial paint --------------------------------------- */

    rerenderTray();
    applyMobileTrayState();
  }
})();
